import React, { useMemo, useState } from 'react';
import TableDataCell from './TransactionsTableTableCell';
import { TransactionType } from './TransactionsTableTableCell/TransactionsTableTableCell';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';

import './TransactionsTableTableRow.scss';
import { Transaction } from '../TransactionsTable';
import { useTransactionsTableContext } from '../../Chart/context/TransactionsTableContext';
import { AskRequest, ReportTransactionDataRow } from '@hone-automation/common';
import { getTransactionFlaggedStatus } from '../utils';

type TransactionsTableTableRowProps = {
  transaction: Transaction;
  attachmentsLoading?: boolean;
  attachmentsData?: ReportTransactionDataRow[] | undefined;
  onRowClick?: (transaction: Transaction) => void;
  cellFlaggedStatus?: (AskRequest & { status: string | undefined }) | undefined;
  cellTrxFlagged?: AskRequest[] | undefined;
};

const TransactionsTableTableRow: React.FC<TransactionsTableTableRowProps> = ({
  transaction,
  attachmentsLoading,
  attachmentsData,
  onRowClick,
  cellFlaggedStatus,
  cellTrxFlagged,
}) => {
  const prefix = prefixClass('transactions-table-cell-row');
  const { reportType, flaggedTransactions, allowFlagging, isConsolidated } = useTransactionsTableContext();
  const [isHovered, setIsHovered] = useState(false);

  const dueDays = useMemo(() => {
    if (transaction.dueDays && typeof transaction.dueDays === 'number' && transaction.dueDays > 0) {
      return `+${transaction.dueDays} d`;
    }
    if (transaction.dueDays && typeof transaction.dueDays === 'number' && transaction.dueDays < 0) {
      return `in ${Math.abs(transaction.dueDays)} d`;
    }
    return 'PAID';
  }, [transaction.dueDays]);

  const flaggedStatus = useMemo(() => {
    return getTransactionFlaggedStatus(transaction, flaggedTransactions, cellTrxFlagged);
  }, [flaggedTransactions, transaction, cellTrxFlagged]);

  const isBeginningBalance = useMemo(() => {
    return transaction.name === 'Beginning Balance' && !transaction.type?.trim() && !transaction.reference?.trim();
  }, [transaction.name, transaction.type, transaction.reference]);

  const shouldAllowFlagging = allowFlagging && !isBeginningBalance;

  const handleRowClick = (trx: Transaction) => {
    if (onRowClick) {
      onRowClick(trx);
    }
  };
  return (
    <tr
      className={classNames(
        prefix(),
        { [prefix('highlight')]: transaction.highlight },
        { [prefix(flaggedStatus)]: !!flaggedStatus }
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableDataCell value={transaction.date} cellType="date" width="90px" color="normal" />
      <TableDataCell
        value={transaction.type}
        cellType="type"
        typeData={transaction.type as TransactionType}
        align="center"
        width="40px"
      />
      <TableDataCell
        width="80px"
        value={transaction.type === 'EXP' ? '-' : transaction.reference}
        cellType="reference"
        align="left"
      />
      <TableDataCell
        value={
          <>
            <b>{transaction.name}</b> {transaction.memo}
          </>
        }
        cellType="string"
        align="left"
      />
      {transaction && (
        <TableDataCell
          value={transaction.links ? transaction.links : ''}
          cellType="links"
          width="28px"
          transaction={transaction}
          attachmentsLoading={attachmentsLoading}
          attachmentsData={attachmentsData}
        />
      )}
      {reportType === 'AP Aging' && (
        <TableDataCell
          width="136px"
          value={
            transaction.dueDays ? (
              <div className={classNames(prefix('due-container'))}>
                <div
                  className={classNames(prefix('due-container-days'), {
                    [prefix('due-container-warning')]:
                      transaction.dueDays && typeof transaction.dueDays === 'number' && transaction.dueDays < 0,
                    [prefix('due-container-error')]:
                      transaction.dueDays && typeof transaction.dueDays === 'number' && transaction.dueDays > 0,
                    [prefix('due-container-paid')]: transaction.dueDays && transaction.dueDays === 'paid',
                  })}
                >
                  {dueDays}
                </div>
                <div className={classNames(prefix('due-container-label'))}>{transaction.dueDate}</div>
              </div>
            ) : (
              <div className={prefix('due-container')}>
                <div className={prefix('due-container-label')}>{transaction.dueDate}</div>
              </div>
            )
          }
          cellType="dueDate"
          align="right"
        />
      )}
      <TableDataCell
        value={transaction.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        cellType="number"
        align="right"
        width="77px"
      />
      {reportType === 'AP Aging' && (
        <TableDataCell
          value={
            transaction.balance
              ? transaction.balance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
              : ''
          }
          cellType="number"
          align="right"
          color="shaded"
          width="68px"
        />
      )}
      <TableDataCell
        value={flaggedStatus ? 'flagged' : isHovered ? 'hovered' : 'not-hovered'}
        cellType="flag"
        align="right"
        onFlagClick={() => handleRowClick(transaction)}
        allowFlagging={shouldAllowFlagging}
        isConsolidated={isConsolidated}
        isBeginningBalance={isBeginningBalance}
      />
    </tr>
  );
};

export default TransactionsTableTableRow;
